/*
  size-chart-lazy.js

  Why this exists
  ---------------
  The size chart modal and the policy popups printed a whole Shopify page
  inside the product page HTML. The size chart page alone holds 26 images.
  A modal is hidden, but the <img> tags are still in the document, so the
  browser downloaded every one of them on every product page load —
  about 1.2 MB before the shopper had even scrolled.

  The markup now sits inside a <template data-lazy-modal>, which the browser
  parses but never fetches. This file moves that markup into the modal the
  first time the modal is about to open, so the images are requested only
  when someone actually asks to see them.

  Three independent triggers, so a modal can never end up empty:
    1. click on the opener button, in the capture phase — runs before the
       theme's own ModalOpener listener calls show()
    2. pointerenter / touchstart on the opener — starts the fetch a moment
       earlier, which makes the chart feel instant
    3. a MutationObserver on the `open` attribute — a safety net for any
       other code path that opens a modal directly (audit-fixes.js, the
       theme editor, deep links)
*/
(function () {
  'use strict';

  /* Safe to include more than once. product-info.js re-fetches the product
     section on every variant change and re-runs the scripts it finds, so a
     second copy must not stack another set of listeners. */
  if (window.__sizeChartLazyReady) return;
  window.__sizeChartLazyReady = true;

  var SELECTOR = 'template[data-lazy-modal]';

  function fill(modal) {
    if (!modal || modal.dataset.lazyFilled === 'true') return;

    var tpl = modal.querySelector(SELECTOR);
    if (!tpl) {
      modal.dataset.lazyFilled = 'true';
      return;
    }

    modal.dataset.lazyFilled = 'true';

    try {
      var frag = tpl.content.cloneNode(true);
      tpl.parentNode.insertBefore(frag, tpl);
      tpl.parentNode.removeChild(tpl);
    } catch (e) {
      /* Nothing clever to do here. Leaving the template in place is
         harmless: the no-JS link to the size chart page still works. */
    }
  }

  function modalFromOpener(opener) {
    if (!opener) return null;
    var sel = opener.dataset ? opener.dataset.modal : null;
    if (!sel) return null;
    try {
      return document.querySelector(sel);
    } catch (e) {
      return null;
    }
  }

  function onEarlyIntent(event) {
    var opener = event.target.closest ? event.target.closest('modal-opener') : null;
    if (!opener) return;
    fill(modalFromOpener(opener));
  }

  /* 1 + 2 — opener driven */
  document.addEventListener('click', onEarlyIntent, true);
  document.addEventListener('pointerenter', onEarlyIntent, true);
  document.addEventListener('touchstart', onEarlyIntent, { capture: true, passive: true });

  /* 3 — safety net: anything that sets [open] on a modal-dialog */
  function watch() {
    if (!('MutationObserver' in window)) return;

    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var el = records[i].target;
        if (el.hasAttribute && el.hasAttribute('open')) fill(el);
      }
    });

    document.querySelectorAll('modal-dialog').forEach(function (modal) {
      if (!modal.querySelector(SELECTOR)) return;
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  /* ModalDialog.connectedCallback() moves each modal to the end of <body>,
     which can happen after DOMContentLoaded. Re-scan once on load so those
     late arrivals are watched too. */
  window.addEventListener('load', watch);
})();
